import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    CloudArrowDown,
    Plugs,
    FloppyDisk,
    ArrowsClockwise,
    CheckCircle,
    XCircle,
    Clock,
    Spinner,
    FolderOpen,
    Warning,
    ArrowRight,
    EnvelopeSimple,
    PaperPlaneTilt,
    Trash,
    Plus,
    Buildings,
    Copy
} from '@phosphor-icons/react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const INTERVAL_OPTIONS = [
    { value: '0', label: 'Deaktiviert' },
    { value: '5', label: 'Alle 5 Minuten' },
    { value: '15', label: 'Alle 15 Minuten' },
    { value: '30', label: 'Alle 30 Minuten' },
];

export default function FTPConfigPage() {
    const [config, setConfig] = useState({
        protocol: 'ftp',
        host: '',
        port: 21,
        username: '',
        password: '',
        remote_path: '/',
        processed_folder: 'verarbeitet',
        default_broker_id: '',
        sync_interval: 0,
        smtp: {
            host: '',
            port: 587,
            username: '',
            password: '',
            use_tls: true,
            sender_email: '',
            sender_name: 'OpenImmo Manager',
        },
        notifications: {
            recipients: [],
            on_error: true,
            on_new: true,
            on_update: false,
            on_every_sync: false,
        },
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [resyncing, setResyncing] = useState(false);
    const [resyncProgress, setResyncProgress] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [syncStatus, setSyncStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [brokers, setBrokers] = useState([]);
    const [configured, setConfigured] = useState(false);
    const [smtpTesting, setSmtpTesting] = useState(false);
    const [smtpTestResult, setSmtpTestResult] = useState(null);
    const [newRecipient, setNewRecipient] = useState('');
    const [smtpFallback, setSmtpFallback] = useState(null);
    const [companyInfo, setCompanyInfo] = useState({
        company: '', contact_name: '', email: '', phone: '', fax: '', street: '', zip: '', city: '', website: ''
    });
    const [savingCompany, setSavingCompany] = useState(false);
    const [dupPreview, setDupPreview] = useState(null);
    const [dupLoading, setDupLoading] = useState(false);
    const [dupCleaning, setDupCleaning] = useState(false);
    const [dupConfirmOpen, setDupConfirmOpen] = useState(false);

    const fetchDuplicates = async () => {
        setDupLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/properties/duplicates/preview`, { withCredentials: true });
            setDupPreview(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Laden der Duplikate');
        }
        setDupLoading(false);
    };

    const runDuplicateCleanup = async () => {
        setDupCleaning(true);
        setDupConfirmOpen(false);
        try {
            const { data } = await axios.post(`${API}/api/properties/duplicates/cleanup`, {}, { withCredentials: true });
            toast.success(`${data.properties_deleted} Duplikate(e) entfernt – ${data.groups_processed} Gruppe(n) bereinigt`);
            await fetchDuplicates();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Bereinigen');
        }
        setDupCleaning(false);
    };

    const fetchConfig = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/ftp/config`, { withCredentials: true });
            
            // Always check for SMTP fallback (even if not configured)
            if (data.smtp_fallback_available) {
                setSmtpFallback(data.smtp_fallback_host);
            }
            
            if (data.configured) {
                setConfig(prev => ({
                    ...prev,
                    protocol: data.protocol || 'ftp',
                    host: data.host || '',
                    port: data.port || 21,
                    username: data.username || '',
                    password: data.password || '',
                    remote_path: data.remote_path || '/',
                    processed_folder: data.processed_folder || 'verarbeitet',
                    default_broker_id: data.default_broker_id || '',
                    sync_interval: data.sync_interval || 0,
                    smtp: {
                        host: data.smtp?.host || '',
                        port: data.smtp?.port || 587,
                        username: data.smtp?.username || '',
                        password: data.smtp?.password || '',
                        use_tls: data.smtp?.use_tls !== false,
                        sender_email: data.smtp?.sender_email || '',
                        sender_name: data.smtp?.sender_name || 'OpenImmo Manager',
                    },
                    notifications: {
                        recipients: data.notifications?.recipients || [],
                        on_error: data.notifications?.on_error !== false,
                        on_new: data.notifications?.on_new !== false,
                        on_update: data.notifications?.on_update || false,
                        on_every_sync: data.notifications?.on_every_sync || false,
                    },
                }));
                setConfigured(true);
            }
        } catch (error) {
            console.error('Error fetching FTP config:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/ftp/status`, { withCredentials: true });
            setSyncStatus(data);
        } catch (error) {
            console.error('Error fetching status:', error);
        }
    }, []);

    const fetchLogs = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/ftp/logs?limit=10`, { withCredentials: true });
            setLogs(data);
        } catch (error) {
            console.error('Error fetching logs:', error);
        }
    }, []);

    const fetchBrokers = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/brokers`, { withCredentials: true });
            setBrokers(data);
        } catch (error) {
            console.error('Error fetching brokers:', error);
        }
    }, []);

    useEffect(() => {
        fetchConfig();
        fetchStatus();
        fetchLogs();
        fetchBrokers();
        // Fetch company info
        axios.get(`${API}/api/company-info`, { withCredentials: true })
            .then(({ data }) => setCompanyInfo(prev => ({ ...prev, ...data })))
            .catch(() => {});
    }, [fetchConfig, fetchStatus, fetchLogs, fetchBrokers]);

    const handleSave = async () => {
        if (!config.host || !config.username) {
            toast.error('Host und Benutzername sind Pflichtfelder');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/api/ftp/config`, config, { withCredentials: true });
            toast.success('FTP-Konfiguration gespeichert');
            setConfigured(true);
            fetchStatus();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await axios.post(`${API}/api/ftp/test`, config, { withCredentials: true });
            setTestResult(data);
            if (data.success) {
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            setTestResult({ success: false, message: 'Verbindungstest fehlgeschlagen' });
            toast.error('Verbindungstest fehlgeschlagen');
        } finally {
            setTesting(false);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setResyncProgress({ status: 'starting', message: 'Wird gestartet...' });
        try {
            const { data } = await axios.post(`${API}/api/ftp/sync`, {}, { withCredentials: true });
            if (!data.success) {
                if (data.message && data.message.includes('läuft bereits')) {
                    setResyncProgress({ status: 'stuck', message: data.message });
                } else {
                    toast.error(data.message || 'Sync fehlgeschlagen');
                    setResyncProgress(null);
                }
                setSyncing(false);
                return;
            }
            pollResyncProgress();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Sync fehlgeschlagen');
            setSyncing(false);
            setResyncProgress(null);
        }
    };

    const handleFullResync = async () => {
        if (!window.confirm('Alle Immobilien vom FTP neu einlesen? Dies verarbeitet alle ZIP-Dateien erneut (inkl. bereits verarbeitete) und aktualisiert fehlende Felder.')) return;
        setResyncing(true);
        setResyncProgress({ status: 'starting', message: 'Wird gestartet...' });
        try {
            const { data } = await axios.post(`${API}/api/ftp/resync-all`, {}, { withCredentials: true });
            if (!data.success) {
                if (data.message && data.message.includes('läuft bereits')) {
                    setResyncProgress({ status: 'stuck', message: data.message });
                } else {
                    toast.error(data.message || 'Resync fehlgeschlagen');
                    setResyncProgress(null);
                }
                setResyncing(false);
                return;
            }
            pollResyncProgress();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Resync fehlgeschlagen');
            setResyncing(false);
            setResyncProgress(null);
        }
    };

    const handleResetSync = async () => {
        try {
            await axios.post(`${API}/api/ftp/resync-reset`, {}, { withCredentials: true });
            setResyncProgress(null);
            setSyncing(false);
            setResyncing(false);
            toast.success('Status zurückgesetzt. Sie können jetzt erneut starten.');
        } catch (error) {
            toast.error('Zurücksetzen fehlgeschlagen');
        }
    };

    const pollResyncProgress = () => {
        const interval = setInterval(async () => {
            try {
                const { data } = await axios.get(`${API}/api/ftp/resync-progress`, { withCredentials: true });
                setResyncProgress(data);
                if (data.status === 'completed' || data.status === 'error' || data.status === 'idle') {
                    clearInterval(interval);
                    setSyncing(false);
                    setResyncing(false);
                    if (data.status === 'completed') {
                        toast.success(data.message || 'Abgeschlossen');
                    } else if (data.status === 'error') {
                        toast.error(data.message || 'Fehlgeschlagen');
                    }
                    fetchLogs();
                }
            } catch (error) {
                clearInterval(interval);
                setSyncing(false);
                setResyncing(false);
            }
        }, 2000);
    };

    const handleFieldChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    const handleSmtpChange = (field, value) => {
        setConfig(prev => ({ ...prev, smtp: { ...prev.smtp, [field]: value } }));
    };

    const handleNotificationChange = (field, value) => {
        setConfig(prev => ({ ...prev, notifications: { ...prev.notifications, [field]: value } }));
    };

    const addRecipient = () => {
        const email = newRecipient.trim();
        if (!email || !email.includes('@')) {
            toast.error('Bitte eine gültige E-Mail-Adresse eingeben');
            return;
        }
        if (config.notifications.recipients.includes(email)) {
            toast.error('Diese E-Mail ist bereits vorhanden');
            return;
        }
        handleNotificationChange('recipients', [...config.notifications.recipients, email]);
        setNewRecipient('');
    };

    const removeRecipient = (email) => {
        handleNotificationChange('recipients', config.notifications.recipients.filter(r => r !== email));
    };

    const handleSmtpTest = async () => {
        if (!config.smtp.host || !config.smtp.username) {
            toast.error('SMTP-Host und Benutzername sind Pflichtfelder');
            return;
        }
        const testRecipient = config.notifications.recipients[0];
        if (!testRecipient) {
            toast.error('Bitte zuerst einen Empfänger hinzufügen');
            return;
        }
        setSmtpTesting(true);
        setSmtpTestResult(null);
        try {
            const { data } = await axios.post(`${API}/api/ftp/smtp/test`, {
                ...config.smtp,
                test_recipient: testRecipient,
            }, { withCredentials: true });
            setSmtpTestResult(data);
            if (data.success) {
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            setSmtpTestResult({ success: false, message: 'SMTP-Test fehlgeschlagen' });
            toast.error('SMTP-Test fehlgeschlagen');
        } finally {
            setSmtpTesting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Spinner className="animate-spin" size={32} />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="ftp-config-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                        Einstellungen
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        FTP/SFTP-Verbindung, E-Mail-Benachrichtigungen und Sync-Konfiguration
                    </p>
                </div>
                <div className="flex gap-2">
                    {configured && (
                        <>
                            <Button
                                variant="outline"
                                onClick={handleFullResync}
                                disabled={resyncing || syncing}
                                className="rounded-sm"
                                data-testid="resync-all-btn"
                            >
                                {resyncing ? (
                                    <Spinner className="animate-spin mr-2" size={16} />
                                ) : (
                                    <ArrowsClockwise size={16} className="mr-2" />
                                )}
                                Alle neu einlesen
                            </Button>
                            <Button
                                onClick={handleSync}
                                disabled={syncing || resyncing}
                                className="rounded-sm"
                                data-testid="sync-now-btn"
                            >
                                {syncing ? (
                                    <Spinner className="animate-spin mr-2" size={16} />
                                ) : (
                                    <ArrowsClockwise size={16} className="mr-2" />
                                )}
                                Jetzt synchronisieren
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Status Bar */}
            {syncStatus && (
                <Card className="border" data-testid="sync-status-card">
                    <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className={`w-2.5 h-2.5 rounded-full ${syncStatus.running ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                                <span className="text-sm font-medium">
                                    {syncStatus.running
                                        ? `Auto-Sync aktiv (alle ${syncStatus.interval_minutes || syncStatus.sync_interval} Min.)`
                                        : 'Auto-Sync deaktiviert'
                                    }
                                </span>
                            </div>
                            {logs.length > 0 && logs[0].completed_at && (
                                <span className="text-xs text-muted-foreground">
                                    Letzter Sync: {new Date(logs[0].completed_at).toLocaleString('de-DE')}
                                </span>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Resync Progress */}
            {resyncProgress && resyncProgress.status === 'running' && (
                <Card className="border border-primary/30 bg-primary/5" data-testid="resync-progress-card">
                    <CardContent className="py-4 space-y-3">
                        <div className="flex items-center gap-3">
                            <Spinner className="animate-spin text-primary" size={18} />
                            <span className="text-sm font-medium">Vollständiger Resync läuft...</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{resyncProgress.message}</p>
                        {resyncProgress.total_files > 0 && (
                            <div className="space-y-2">
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>Datei {resyncProgress.current_file_index} von {resyncProgress.total_files}</span>
                                    <span>{resyncProgress.properties_processed || 0} Immobilien verarbeitet</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                    <div
                                        className="bg-primary h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.round((resyncProgress.current_file_index / resyncProgress.total_files) * 100)}%` }}
                                    />
                                </div>
                                <div className="flex gap-4 text-xs">
                                    <span className="text-green-600">{resyncProgress.properties_updated || 0} aktualisiert</span>
                                    <span className="text-blue-600">{resyncProgress.properties_created || 0} neu</span>
                                    {resyncProgress.errors?.length > 0 && (
                                        <span className="text-red-600">{resyncProgress.errors.length} Fehler</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Stuck Sync - Reset Button */}
            {resyncProgress && resyncProgress.status === 'stuck' && (
                <Card className="border border-orange-200 bg-orange-50" data-testid="resync-stuck-card">
                    <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-orange-800">{resyncProgress.message}</span>
                            <Button variant="outline" size="sm" className="text-xs border-orange-300" onClick={handleResetSync}>
                                Status zurücksetzen
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Resync Completed */}
            {resyncProgress && resyncProgress.status === 'completed' && (
                <Card className="border border-green-200 bg-green-50" data-testid="resync-completed-card">
                    <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CheckCircle size={18} className="text-green-600" weight="fill" />
                                <span className="text-sm font-medium text-green-800">{resyncProgress.message}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setResyncProgress(null)}>
                                Schließen
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Resync Error */}
            {resyncProgress && resyncProgress.status === 'error' && (
                <Card className="border border-red-200 bg-red-50" data-testid="resync-error-card">
                    <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-red-800">{resyncProgress.message}</span>
                            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setResyncProgress(null)}>
                                Schließen
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Configuration */}
                <div className="lg:col-span-2 space-y-6">
                    <Card className="border" data-testid="ftp-connection-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                <Plugs size={20} weight="fill" />
                                Verbindung
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-xs font-medium">Protokoll</Label>
                                    <Select value={config.protocol} onValueChange={(v) => handleFieldChange('protocol', v)}>
                                        <SelectTrigger className="rounded-sm mt-1.5" data-testid="protocol-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="ftp">FTP</SelectItem>
                                            <SelectItem value="sftp">SFTP</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-medium">Host</Label>
                                    <Input
                                        value={config.host}
                                        onChange={(e) => handleFieldChange('host', e.target.value)}
                                        placeholder="ftp.example.com"
                                        className="rounded-sm mt-1.5"
                                        data-testid="ftp-host-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-medium">Port</Label>
                                    <Input
                                        type="number"
                                        value={config.port}
                                        onChange={(e) => handleFieldChange('port', parseInt(e.target.value) || 21)}
                                        className="rounded-sm mt-1.5"
                                        data-testid="ftp-port-input"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-medium">Benutzername</Label>
                                    <Input
                                        value={config.username}
                                        onChange={(e) => handleFieldChange('username', e.target.value)}
                                        className="rounded-sm mt-1.5"
                                        data-testid="ftp-username-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-medium">Passwort</Label>
                                    <Input
                                        type="password"
                                        value={config.password}
                                        onChange={(e) => handleFieldChange('password', e.target.value)}
                                        className="rounded-sm mt-1.5"
                                        data-testid="ftp-password-input"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-medium">FTP-Ordner</Label>
                                    <Input
                                        value={config.remote_path}
                                        onChange={(e) => handleFieldChange('remote_path', e.target.value)}
                                        placeholder="/openimmo"
                                        className="rounded-sm mt-1.5"
                                        data-testid="ftp-path-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-medium">Verarbeitet-Ordner</Label>
                                    <Input
                                        value={config.processed_folder}
                                        onChange={(e) => handleFieldChange('processed_folder', e.target.value)}
                                        placeholder="verarbeitet"
                                        className="rounded-sm mt-1.5"
                                        data-testid="ftp-processed-folder-input"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={handleTest}
                                    disabled={testing || !config.host}
                                    className="rounded-sm"
                                    data-testid="test-connection-btn"
                                >
                                    {testing ? (
                                        <Spinner className="animate-spin mr-2" size={16} />
                                    ) : (
                                        <Plugs size={16} className="mr-2" />
                                    )}
                                    Verbindung testen
                                </Button>
                                {testResult && (
                                    <div className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                        {testResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
                                        {testResult.message}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Sync Settings */}
                    <Card className="border" data-testid="sync-settings-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                <Clock size={20} weight="fill" />
                                Sync-Einstellungen
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-medium">Auto-Sync Intervall</Label>
                                    <Select
                                        value={String(config.sync_interval)}
                                        onValueChange={(v) => handleFieldChange('sync_interval', parseInt(v))}
                                    >
                                        <SelectTrigger className="rounded-sm mt-1.5" data-testid="sync-interval-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {INTERVAL_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-medium">Standard-Makler</Label>
                                    <Select
                                        value={config.default_broker_id || 'none'}
                                        onValueChange={(v) => handleFieldChange('default_broker_id', v === 'none' ? '' : v)}
                                    >
                                        <SelectTrigger className="rounded-sm mt-1.5" data-testid="default-broker-select">
                                            <SelectValue placeholder="Kein Makler" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Kein Makler</SelectItem>
                                            {brokers.map((b) => (
                                                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="bg-muted/50 rounded-sm p-3 text-sm text-muted-foreground">
                                <p className="flex items-start gap-2">
                                    <ArrowRight size={14} className="mt-0.5 shrink-0" />
                                    <span>
                                        Der Import erkennt automatisch ob ein Objekt <strong>neu</strong> ist oder <strong>aktualisiert</strong> werden muss
                                        (über Objektnr. extern und OpenImmo-ID). Verarbeitete ZIP-Dateien werden in den
                                        <strong> {config.processed_folder || 'verarbeitet'}</strong>-Ordner verschoben.
                                    </span>
                                </p>
                            </div>

                            <Button
                                onClick={handleSave}
                                disabled={saving || !config.host}
                                className="rounded-sm"
                                data-testid="save-config-btn"
                            >
                                {saving ? (
                                    <Spinner className="animate-spin mr-2" size={16} />
                                ) : (
                                    <FloppyDisk size={16} className="mr-2" />
                                )}
                                Konfiguration speichern
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Email Notifications */}
                    <Card className="border" data-testid="email-config-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                <EnvelopeSimple size={20} weight="fill" />
                                E-Mail-Benachrichtigungen
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-5">
                            {/* SMTP Fallback Hint */}
                            {smtpFallback && !config.smtp.host && (
                                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-700" data-testid="smtp-fallback-hint">
                                    <p>Kein eigener SMTP-Server konfiguriert. E-Mails werden über den Admin-Server (<strong>{smtpFallback}</strong>) versendet. Sie können optional Ihre eigenen SMTP-Daten hinterlegen.</p>
                                </div>
                            )}
                            {/* SMTP Settings */}
                            <div>
                                <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">SMTP-Server</p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <Label className="text-xs font-medium">SMTP-Host</Label>
                                        <Input
                                            value={config.smtp.host}
                                            onChange={(e) => handleSmtpChange('host', e.target.value)}
                                            placeholder="smtp.example.com"
                                            className="rounded-sm mt-1.5"
                                            data-testid="smtp-host-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-medium">Port</Label>
                                        <Input
                                            type="number"
                                            value={config.smtp.port}
                                            onChange={(e) => handleSmtpChange('port', parseInt(e.target.value) || 587)}
                                            className="rounded-sm mt-1.5"
                                            data-testid="smtp-port-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-medium">TLS/SSL</Label>
                                        <Select
                                            value={config.smtp.use_tls ? 'true' : 'false'}
                                            onValueChange={(v) => handleSmtpChange('use_tls', v === 'true')}
                                        >
                                            <SelectTrigger className="rounded-sm mt-1.5" data-testid="smtp-tls-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="true">Aktiviert</SelectItem>
                                                <SelectItem value="false">Deaktiviert</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <Label className="text-xs font-medium">SMTP-Benutzername</Label>
                                        <Input
                                            value={config.smtp.username}
                                            onChange={(e) => handleSmtpChange('username', e.target.value)}
                                            className="rounded-sm mt-1.5"
                                            data-testid="smtp-username-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-medium">SMTP-Passwort</Label>
                                        <Input
                                            type="password"
                                            value={config.smtp.password}
                                            onChange={(e) => handleSmtpChange('password', e.target.value)}
                                            className="rounded-sm mt-1.5"
                                            data-testid="smtp-password-input"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <Label className="text-xs font-medium">Absender-E-Mail</Label>
                                        <Input
                                            value={config.smtp.sender_email}
                                            onChange={(e) => handleSmtpChange('sender_email', e.target.value)}
                                            placeholder="noreply@example.com"
                                            className="rounded-sm mt-1.5"
                                            data-testid="smtp-sender-email-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-medium">Absender-Name</Label>
                                        <Input
                                            value={config.smtp.sender_name}
                                            onChange={(e) => handleSmtpChange('sender_name', e.target.value)}
                                            placeholder="OpenImmo Manager"
                                            className="rounded-sm mt-1.5"
                                            data-testid="smtp-sender-name-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Recipients */}
                            <div>
                                <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">Empfänger</p>
                                <div className="flex gap-2">
                                    <Input
                                        value={newRecipient}
                                        onChange={(e) => setNewRecipient(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                                        placeholder="email@example.com"
                                        className="rounded-sm"
                                        data-testid="recipient-input"
                                    />
                                    <Button
                                        variant="outline"
                                        onClick={addRecipient}
                                        className="rounded-sm shrink-0"
                                        data-testid="add-recipient-btn"
                                    >
                                        <Plus size={16} className="mr-1" />
                                        Hinzufügen
                                    </Button>
                                </div>
                                {config.notifications.recipients.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {config.notifications.recipients.map((email) => (
                                            <div
                                                key={email}
                                                className="flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-sm text-sm"
                                                data-testid={`recipient-${email}`}
                                            >
                                                <EnvelopeSimple size={14} className="text-muted-foreground" />
                                                {email}
                                                <button
                                                    onClick={() => removeRecipient(email)}
                                                    className="text-muted-foreground hover:text-destructive ml-1"
                                                    data-testid={`remove-recipient-${email}`}
                                                >
                                                    <XCircle size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Notification Triggers */}
                            <div>
                                <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">Benachrichtigen bei</p>
                                <div className="space-y-3">
                                    {[
                                        { key: 'on_error', label: 'Fehler beim Sync', desc: 'Wenn der Import fehlschlägt oder Dateien nicht verarbeitet werden können' },
                                        { key: 'on_new', label: 'Neue Immobilien', desc: 'Wenn neue Objekte erstmals importiert werden' },
                                        { key: 'on_update', label: 'Aktualisierungen', desc: 'Wenn bestehende Objekte aktualisiert werden' },
                                        { key: 'on_every_sync', label: 'Jeder Sync-Vorgang', desc: 'Bei jedem Sync eine Zusammenfassung erhalten' },
                                    ].map(({ key, label, desc }) => (
                                        <label
                                            key={key}
                                            className="flex items-start gap-3 cursor-pointer group"
                                            data-testid={`notification-${key}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={config.notifications[key] || false}
                                                onChange={(e) => handleNotificationChange(key, e.target.checked)}
                                                className="mt-0.5 rounded border-gray-300"
                                            />
                                            <div>
                                                <p className="text-sm font-medium group-hover:text-primary transition-colors">{label}</p>
                                                <p className="text-xs text-muted-foreground">{desc}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Test Email Button */}
                            <div className="flex gap-2 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={handleSmtpTest}
                                    disabled={smtpTesting || !config.smtp.host || config.notifications.recipients.length === 0}
                                    className="rounded-sm"
                                    data-testid="test-smtp-btn"
                                >
                                    {smtpTesting ? (
                                        <Spinner className="animate-spin mr-2" size={16} />
                                    ) : (
                                        <PaperPlaneTilt size={16} className="mr-2" />
                                    )}
                                    Test-E-Mail senden
                                </Button>
                                {smtpTestResult && (
                                    <div className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${smtpTestResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                        {smtpTestResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
                                        {smtpTestResult.message}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Sync Log Sidebar */}
                <div className="space-y-6">
                    <Card className="border" data-testid="sync-log-card">
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                <span className="flex items-center gap-2">
                                    <FolderOpen size={20} weight="fill" />
                                    Sync-Protokoll
                                </span>
                                <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8" onClick={fetchLogs}>
                                    <ArrowsClockwise size={14} />
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {logs.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-8">
                                    Noch keine Synchronisierungen durchgeführt
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {logs.map((log, idx) => (
                                        <div key={idx} className="border rounded-sm p-3 space-y-2" data-testid={`sync-log-${idx}`}>
                                            <div className="flex items-center justify-between">
                                                <Badge
                                                    variant="outline"
                                                    className={`rounded-sm text-xs ${
                                                        log.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
                                                        log.status === 'error' ? 'bg-red-50 text-red-700 border-red-200' :
                                                        'bg-yellow-50 text-yellow-700 border-yellow-200'
                                                    }`}
                                                >
                                                    {log.status === 'completed' ? 'Erfolgreich' : log.status === 'error' ? 'Fehler' : 'Läuft'}
                                                </Badge>
                                                <span className="text-xs text-muted-foreground">
                                                    {log.triggered_by === 'manual' ? 'Manuell' : 'Auto'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                {new Date(log.started_at).toLocaleString('de-DE')}
                                            </p>
                                            {log.message && (
                                                <p className="text-xs">{log.message}</p>
                                            )}
                                            <div className="flex gap-3 text-xs">
                                                <span className="text-muted-foreground">
                                                    Dateien: <strong>{log.files_found}</strong>
                                                </span>
                                                {log.properties_created > 0 && (
                                                    <span className="text-green-600">
                                                        +{log.properties_created} neu
                                                    </span>
                                                )}
                                                {log.properties_updated > 0 && (
                                                    <span className="text-blue-600">
                                                        {log.properties_updated} aktualisiert
                                                    </span>
                                                )}
                                            </div>
                                            {log.errors && log.errors.length > 0 && (
                                                <div className="text-xs text-red-600 space-y-0.5">
                                                    {log.errors.map((err, i) => (
                                                        <p key={i} className="flex items-start gap-1">
                                                            <Warning size={12} className="mt-0.5 shrink-0" />
                                                            {err}
                                                        </p>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Firmendaten / Kontaktdaten */}
            <Card className="border" data-testid="company-info-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Buildings size={20} />
                        Firmendaten (Standard-Kontakt)
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                        Diese Daten werden als Kontakt auf öffentlichen Exposés angezeigt, wenn kein spezifischer Makler zugewiesen ist.
                    </p>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Firmenname</Label>
                            <Input
                                value={companyInfo.company}
                                onChange={(e) => setCompanyInfo(p => ({ ...p, company: e.target.value }))}
                                placeholder="z.B. Claus Blumenauer Immobilien"
                                className="rounded-sm"
                                data-testid="company-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Ansprechpartner</Label>
                            <Input
                                value={companyInfo.contact_name}
                                onChange={(e) => setCompanyInfo(p => ({ ...p, contact_name: e.target.value }))}
                                placeholder="Vor- und Nachname"
                                className="rounded-sm"
                                data-testid="company-contact-input"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>E-Mail</Label>
                            <Input
                                value={companyInfo.email}
                                onChange={(e) => setCompanyInfo(p => ({ ...p, email: e.target.value }))}
                                placeholder="mail@example.de"
                                className="rounded-sm"
                                data-testid="company-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Telefon</Label>
                            <Input
                                value={companyInfo.phone}
                                onChange={(e) => setCompanyInfo(p => ({ ...p, phone: e.target.value }))}
                                placeholder="06174-96100"
                                className="rounded-sm"
                                data-testid="company-phone-input"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-2 space-y-2">
                            <Label>Straße & Hausnummer</Label>
                            <Input
                                value={companyInfo.street}
                                onChange={(e) => setCompanyInfo(p => ({ ...p, street: e.target.value }))}
                                placeholder="Frankfurter Str. 1"
                                className="rounded-sm"
                                data-testid="company-street-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>PLZ</Label>
                            <Input
                                value={companyInfo.zip}
                                onChange={(e) => setCompanyInfo(p => ({ ...p, zip: e.target.value }))}
                                placeholder="61462"
                                className="rounded-sm"
                                data-testid="company-zip-input"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Stadt</Label>
                            <Input
                                value={companyInfo.city}
                                onChange={(e) => setCompanyInfo(p => ({ ...p, city: e.target.value }))}
                                placeholder="Königstein im Taunus"
                                className="rounded-sm"
                                data-testid="company-city-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Website</Label>
                            <Input
                                value={companyInfo.website}
                                onChange={(e) => setCompanyInfo(p => ({ ...p, website: e.target.value }))}
                                placeholder="https://www.example.de"
                                className="rounded-sm"
                                data-testid="company-website-input"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button
                            onClick={async () => {
                                setSavingCompany(true);
                                try {
                                    await axios.put(`${API}/api/company-info`, companyInfo, { withCredentials: true });
                                    toast.success('Firmendaten gespeichert');
                                } catch (e) {
                                    toast.error('Fehler beim Speichern');
                                }
                                setSavingCompany(false);
                            }}
                            disabled={savingCompany}
                            className="rounded-sm"
                            data-testid="save-company-btn"
                        >
                            {savingCompany ? <Spinner className="animate-spin mr-2" size={16} /> : <FloppyDisk size={16} className="mr-2" />}
                            Firmendaten speichern
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Duplikate bereinigen */}
            <Card className="border border-amber-200" data-testid="duplicates-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                            <Copy size={20} weight="fill" className="text-amber-600" />
                            Duplikate bereinigen
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            Findet Immobilien, die mehrfach in der Datenbank existieren (z.&nbsp;B. nach mehrfachem Re-Import). Pro Gruppe wird das zuletzt aktualisierte Objekt behalten, ältere Varianten werden gelöscht.
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={fetchDuplicates}
                                disabled={dupLoading}
                                className="rounded-sm"
                                data-testid="preview-duplicates-btn"
                            >
                                {dupLoading ? <Spinner className="animate-spin mr-2" size={16} /> : <ArrowsClockwise size={16} className="mr-2" />}
                                Vorschau aktualisieren
                            </Button>
                            {dupPreview && dupPreview.properties_to_remove > 0 && (
                                <Button
                                    variant="destructive"
                                    onClick={() => setDupConfirmOpen(true)}
                                    disabled={dupCleaning}
                                    className="rounded-sm"
                                    data-testid="cleanup-duplicates-btn"
                                >
                                    {dupCleaning ? <Spinner className="animate-spin mr-2" size={16} /> : <Trash size={16} className="mr-2" />}
                                    {dupPreview.properties_to_remove} Duplikat(e) jetzt löschen
                                </Button>
                            )}
                        </div>

                        {dupPreview && (
                            <div className="border rounded-sm p-4 bg-amber-50/30">
                                <div className="flex items-center gap-4 text-sm font-medium mb-3">
                                    <span data-testid="dup-groups-count">Gruppen: <strong>{dupPreview.groups_found}</strong></span>
                                    <span data-testid="dup-remove-count">Zu löschen: <strong className="text-destructive">{dupPreview.properties_to_remove}</strong></span>
                                </div>
                                {dupPreview.groups_found === 0 ? (
                                    <div className="flex items-center gap-2 text-green-700 text-sm">
                                        <CheckCircle size={16} weight="fill" />
                                        Keine Duplikate gefunden – Datenbank ist sauber.
                                    </div>
                                ) : (
                                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                        {dupPreview.duplicate_groups.slice(0, 50).map((g, idx) => (
                                            <div key={idx} className="border rounded-sm bg-white p-3" data-testid={`dup-group-${idx}`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Badge variant="outline" className="text-[10px]">
                                                        {g.match_type === 'ext' ? 'Objekt-Nr' : g.match_type === 'oid' ? 'OpenImmo-ID' : 'Titel+Adresse'}
                                                    </Badge>
                                                    <code className="text-xs text-muted-foreground truncate">{g.match_key.split('::')[1]}</code>
                                                </div>
                                                <div className="text-xs space-y-1">
                                                    <div className="flex items-start gap-2">
                                                        <Badge className="bg-green-100 text-green-800 border-green-300 hover:bg-green-100 shrink-0">Behalten</Badge>
                                                        <div className="flex-1">
                                                            <div className="font-medium truncate">{g.keep.title || '(ohne Titel)'}</div>
                                                            <div className="text-muted-foreground">Status: {g.keep.status} · Preis: {g.keep.price?.toLocaleString('de-DE')} € · Stand: {g.keep.updated_at?.slice(0,16).replace('T',' ')}</div>
                                                        </div>
                                                    </div>
                                                    {g.remove.map((r, ri) => (
                                                        <div key={ri} className="flex items-start gap-2">
                                                            <Badge className="bg-red-100 text-red-800 border-red-300 hover:bg-red-100 shrink-0">Löschen</Badge>
                                                            <div className="flex-1">
                                                                <div className="font-medium truncate">{r.title || '(ohne Titel)'}</div>
                                                                <div className="text-muted-foreground">Status: {r.status} · Preis: {r.price?.toLocaleString('de-DE')} € · Stand: {r.updated_at?.slice(0,16).replace('T',' ')}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                        {dupPreview.duplicate_groups.length > 50 && (
                                            <p className="text-xs text-muted-foreground italic">… und {dupPreview.duplicate_groups.length - 50} weitere Gruppen (werden alle bereinigt)</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {!dupPreview && !dupLoading && (
                            <p className="text-xs text-muted-foreground italic">Klicke auf „Vorschau aktualisieren“, um die Duplikatsuche zu starten.</p>
                        )}
                    </CardContent>
                </Card>

            <AlertDialog open={dupConfirmOpen} onOpenChange={setDupConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Duplikate endgültig löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Es werden <strong>{dupPreview?.properties_to_remove}</strong> Immobilie(n) aus der Datenbank entfernt. Pro Duplikatsgruppe bleibt das zuletzt aktualisierte Objekt erhalten. Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="dup-confirm-cancel">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runDuplicateCleanup}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="dup-confirm-delete"
                        >
                            Ja, löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
